import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import { useAuth } from '../Auth/AuthProvider';
import { createTenantAdminApi, adminError } from './api';

const AdminApiContext = createContext(null);
export function TenantAdminProvider({ children }) {
  const { session } = useAuth();
  const api = useMemo(() => createTenantAdminApi(session.api), [session]);
  return <AdminApiContext.Provider value={api}>{children}</AdminApiContext.Provider>;
}
export const useAdminApi = () => useContext(AdminApiContext);

export function useAdminResource(loader, errorMessage = adminError) {
  const sequence = useRef(0);
  const [state, setState] = useState({ loading: true, data: null, error: '' });
  const reload = useCallback(async () => {
    const current = ++sequence.current;
    setState({ loading: true, data: null, error: '' });
    try {
      const data = await loader();
      if (current === sequence.current) setState({ loading: false, data, error: '' });
      return true;
    } catch (error) {
      if (current === sequence.current) setState({ loading: false, data: null, error: errorMessage(error) });
      return false;
    }
  }, [loader, errorMessage]);
  const invalidate = useCallback(() => { sequence.current++; }, []);
  useEffect(() => { reload(); return invalidate; }, [reload, invalidate]);
  return { ...state, reload };
}

export function ResourceState({ resource, children }) {
  if (resource.loading) return <p role="status"><Spinner as="span" size="sm" aria-hidden="true" /> Loading…</p>;
  if (resource.error) return <Alert variant="danger">{resource.error}{' '}<Button variant="outline-secondary" onClick={resource.reload}>Retry</Button></Alert>;
  return children;
}
export function PageTitle({ title, children }) { return <div className="ta-page-title"><h1>{title}</h1>{children}</div>; }
export function Details({ values }) {
  return <dl className="ta-details">{values.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value ?? 'Not provided'}</dd></div>)}</dl>;
}
export function Seats({ seats }) {
  return <section aria-label="Seat usage" className="ta-seats">{[
    ['Purchased seats', seats?.purchased], ['Used seats', seats?.used], ['Available seats', seats?.available]
  ].map(([label, value]) => <Card key={label}><Card.Body><span>{label}</span><strong>{value ?? 'Not provided'}</strong></Card.Body></Card>)}</section>;
}
export function formatDate(value) {
  if (!value) return 'Not provided';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not provided' : date.toLocaleString();
}
export function SsoDetails({ sso }) {
  if (!sso) return <p>SSO status is unavailable.</p>;
  return <Details values={[
    ['SSO configuration', sso.configured ? 'Configured' : 'Not configured'],
    ['SSO enabled', sso.enabled ? 'Yes' : 'No'],
    ['Issuer', sso.issuer], ['Scopes', sso.scopes],
    ['Last successful reachability check', formatDate(sso.lastTestedAt)],
  ]} />;
}
